import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { customBilling } from '@/api/customBilling';
import type { CustomInvoiceItemRow } from '@/api/customBilling';
import type { ApiError } from '@/api/client';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useToast } from '@/hooks/use-toast';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, ArrowLeft, Printer, CreditCard } from 'lucide-react';
import {
  InvoiceStatusPill, formatMoney, formatDateOnly, formatDateTime, formatQty,
} from './customInvoiceShared';

function MetaField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">{label}</div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

export function CustomInvoiceDetail() {
  const { id } = useParams<{ id: string }>();
  const user = useCurrentUser();
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data, isLoading, error } = useQuery({
    queryKey: ['customInvoice', id],
    queryFn: () => customBilling.getInvoice(id!),
    enabled: !!id,
  });

  // Super-admin only — the backend charge endpoint needs cross-tenant scope
  // to decrypt the customer's gateway creds. Declines come back as HTTP 402
  // (apiFetch throws; the ChargeInvoiceResult rides on ApiError.details).
  const charge = useMutation({
    mutationFn: () => customBilling.chargeInvoice(id!),
    onSuccess: (result) => {
      toast({
        title: 'Invoice charged',
        description: `Payment captured — status is now ${result.status}.`,
        className: 'border-emerald-300 bg-emerald-50 text-emerald-900',
      });
      qc.invalidateQueries({ queryKey: ['customInvoice', id] });
      qc.invalidateQueries({ queryKey: ['customInvoices'] });
    },
    onError: (err: unknown) => {
      const details = (err as ApiError | undefined)?.details as
        | { errorMessage?: string | null; errorCode?: string | null }
        | undefined;
      toast({
        variant: 'destructive',
        title: 'Charge failed',
        description: details?.errorMessage
          ?? (err instanceof Error ? err.message : 'The gateway declined the charge.'),
      });
      // Status may have moved draft → issued/failed even on decline.
      qc.invalidateQueries({ queryKey: ['customInvoice', id] });
      qc.invalidateQueries({ queryKey: ['customInvoices'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <>
        <PageHeader eyebrow="Billing" title="Invoice" />
        <div role="alert" className="text-sm text-destructive">
          {error instanceof Error ? error.message : 'Invoice not found.'}
        </div>
      </>
    );
  }

  const { invoice, items } = data;
  const chargeable = user.role === 'super_admin' && (invoice.status === 'draft' || invoice.status === 'failed');

  return (
    <>
      <PageHeader
        eyebrow="Billing"
        title={`Invoice ${invoice.number}`}
        description={`${invoice.customer_name ?? 'Customer'} · ${formatDateOnly(invoice.period_start)} – ${formatDateOnly(invoice.period_end)}`}
        actions={
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to="/billing/custom-invoices"><ArrowLeft className="h-4 w-4 mr-1.5" /> All invoices</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to={`/billing/custom-invoices/${invoice.id}/print`}><Printer className="h-4 w-4 mr-1.5" /> Print view</Link>
            </Button>
            {chargeable && (
              <Button size="sm" disabled={charge.isPending} onClick={() => charge.mutate()}>
                <CreditCard className="h-4 w-4 mr-1.5" />
                {charge.isPending ? 'Charging…' : 'Charge now'}
              </Button>
            )}
          </div>
        }
      />

      <div className="space-y-5 max-w-3xl">
        {/* Invoice meta */}
        <Card>
          <CardContent className="pt-5">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4">
              <MetaField label="Customer">
                <span className="font-medium">{invoice.customer_name ?? '—'}</span>
                {invoice.customer_number != null && (
                  <span className="text-xs text-muted-foreground font-mono ms-1.5">
                    #{String(invoice.customer_number).padStart(4, '0')}
                  </span>
                )}
              </MetaField>
              <MetaField label="Period">
                {formatDateOnly(invoice.period_start)} – {formatDateOnly(invoice.period_end)}
              </MetaField>
              <MetaField label="Status"><InvoiceStatusPill status={invoice.status} /></MetaField>
              <MetaField label="Issued">{formatDateTime(invoice.issued_at)}</MetaField>
              <MetaField label="Due">{formatDateTime(invoice.due_at)}</MetaField>
              <MetaField label="Paid">
                {invoice.paid_at ? (
                  <span className="text-emerald-700 font-medium">{formatDateTime(invoice.paid_at)}</span>
                ) : '—'}
              </MetaField>
            </div>
            {invoice.notes && (
              <p className="mt-4 pt-4 border-t border-border text-sm text-muted-foreground whitespace-pre-wrap">
                {invoice.notes}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Line items */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Line items</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 border-b border-border">
                  <tr>
                    <th className="px-4 py-2.5 text-start text-xs font-semibold uppercase tracking-wider text-muted-foreground">Description</th>
                    <th className="px-4 py-2.5 text-end text-xs font-semibold uppercase tracking-wider text-muted-foreground">Qty</th>
                    <th className="px-4 py-2.5 text-end text-xs font-semibold uppercase tracking-wider text-muted-foreground">Unit price</th>
                    <th className="px-4 py-2.5 text-end text-xs font-semibold uppercase tracking-wider text-muted-foreground">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground text-sm">
                        No line items on this invoice.
                      </td>
                    </tr>
                  ) : items.map((it: CustomInvoiceItemRow) => (
                    <tr key={it.id} className="border-b border-border/50 last:border-b-0">
                      <td className="px-4 py-2.5">{it.description}</td>
                      <td className="px-4 py-2.5 text-end tabular-nums text-muted-foreground">{formatQty(it.quantity)}</td>
                      <td className="px-4 py-2.5 text-end tabular-nums text-muted-foreground">{formatMoney(it.unit_price_cents)}</td>
                      <td className="px-4 py-2.5 text-end tabular-nums font-medium">{formatMoney(it.amount_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals ledger */}
            <div className="mt-4 flex justify-end">
              <div className="w-full sm:w-64 space-y-1.5 text-sm">
                <div className="flex justify-between text-muted-foreground">
                  <span>Subtotal</span>
                  <span className="tabular-nums">{formatMoney(invoice.subtotal_cents)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Tax</span>
                  <span className="tabular-nums">{formatMoney(invoice.tax_cents)}</span>
                </div>
                <div className="flex justify-between font-semibold text-base pt-2 border-t border-border">
                  <span>Total</span>
                  <span className="tabular-nums">{formatMoney(invoice.total_cents)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
